/**
 * Tab-scoped token storage for admin-issued "sign in as vendor" sessions
 * (KAN-137).
 *
 * The SDK's default token storage is `window.localStorage`, shared across
 * every tab of this origin. An operator opening "sign in as vendor" for
 * Seller A, then - without closing that tab - opening it again for Seller B,
 * would have the second tab's redemption silently overwrite the first tab's
 * session: the next background refetch (e.g. on window focus) or save in
 * tab 1 would go out as Seller B. That is exactly the failure the ticket
 * exists to prevent.
 *
 * Fix: an impersonation session is written to `sessionStorage` instead,
 * which is strictly per-tab. A fresh tab opened via `window.open()` from
 * admin-panel - a different origin than vendor-panel - always starts with
 * empty `sessionStorage`, so two independently-opened impersonation tabs can
 * never share one. A normal `/login` session is untouched: with the
 * `IMPERSONATING_FLAG` unset, every function here falls straight through to
 * `localStorage`, exactly as before this file existed.
 *
 * This is the ONLY place that decides "sessionStorage or localStorage" -
 * `fetchQuery`/`importProductsQuery`/`uploadFilesQuery` and the Medusa SDK's
 * own storage config (see `client.ts`) all route through these three
 * functions rather than touching `localStorage`/`sessionStorage` directly,
 * so no code path can silently bypass the resolver.
 */

const TOKEN_KEY = "medusa_auth_token"
const IMPERSONATING_FLAG = "ss_impersonating"

function activeStore(): Storage {
  return sessionStorage.getItem(IMPERSONATING_FLAG) ? sessionStorage : localStorage
}

export function getAuthToken(): string {
  return activeStore().getItem(TOKEN_KEY) || ""
}

export function setAuthToken(token: string, opts?: { impersonating?: boolean }): void {
  if (opts?.impersonating) {
    sessionStorage.setItem(IMPERSONATING_FLAG, "1")
  }
  activeStore().setItem(TOKEN_KEY, token)
}

export function clearAuthToken(): void {
  const impersonating = Boolean(sessionStorage.getItem(IMPERSONATING_FLAG))
  ;(impersonating ? sessionStorage : localStorage).removeItem(TOKEN_KEY)
  if (impersonating) {
    sessionStorage.removeItem(IMPERSONATING_FLAG)
  }
}

/** The SDK's `auth.storage` config - see client.ts's `sdk` instantiation. */
export const authTokenStorage = {
  getItem: (key: string) => activeStore().getItem(key),
  setItem: (key: string, value: string) => activeStore().setItem(key, value),
  removeItem: (key: string) => activeStore().removeItem(key),
}
